import { useEffect } from "react";

/**
 * Injects a `<script type="application/ld+json">` block for the current
 * page and removes it on unmount, mirroring the lifecycle of `useSeo`.
 *
 * Each block is keyed by a stable `id` so re-renders replace the same
 * node instead of accumulating duplicates. Pass `null`/`undefined` when
 * the data isn't ready yet (e.g. a property still loading) — nothing is
 * rendered until real structured data exists.
 *
 * Google executes JS before indexing, so this per-page structured data
 * is picked up for rich results (listings, breadcrumbs, articles) even
 * though routing is client-side.
 */
export function useJsonLd(id: string, data: object | null | undefined) {
  useEffect(() => {
    if (!data) return;

    const scriptId = `ld-${id}`;
    let el = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [id, data]);
}
